import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../lib/utils';

// Simple Slot: renders the single child element with merged props (replaces @radix-ui/react-slot)
function Slot({ children, ...slotProps }: React.HTMLAttributes<HTMLElement>): JSX.Element {
  if (!React.isValidElement<React.HTMLAttributes<HTMLElement>>(children)) {
    throw new Error('Slot requires a single valid React element child');
  }
  // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
  return React.cloneElement(children, {
    ...slotProps,
    ...children.props,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    className: cn((slotProps as { className?: string }).className, children.props.className),
  });
}

const badgeVariants = cva(
  'inline-flex items-center justify-center rounded-md border px-2 py-0.5 text-xs font-medium w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden',
  {
    variants: {
      variant: {
        default: 'border-transparent bg-primary text-primary-foreground [a&]:hover:bg-primary/90',
        secondary: 'border-transparent bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/90',
        destructive: 'border-transparent bg-destructive text-white [a&]:hover:bg-destructive/90 focus-visible:ring-destructive/20',
        outline: 'text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

function Badge({
  className,
  variant,
  asChild = false,
  ...props
}: React.ComponentProps<'span'> &
VariantProps<typeof badgeVariants> & { asChild?: boolean }): JSX.Element {
  const Comp = asChild ? Slot : 'span';

  return (
    <Comp
      data-slot='badge'
      className={cn(badgeVariants({ variant }), className)}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
    />
  );
}

Badge.defaultProps = { asChild: false };

export { Badge, badgeVariants };
